import { useEffect, useMemo, useState, useCallback } from 'react'
import {
  Box, Stack, TextField, Pagination, LinearProgress, Typography, Button, PaginationItem
} from '@mui/material'
import { useSearchParams } from 'react-router-dom'
import { useIsMobile } from '@/hooks/Responsive'
import { visuallyHidden } from '@mui/utils'
import KeyboardArrowLeftIcon from '@mui/icons-material/KeyboardArrowLeft'
import KeyboardArrowRightIcon from '@mui/icons-material/KeyboardArrowRight'

export default function BoardList({
  view = 'table',
  columns = [],
  items,
  fetcher,
  getRowId = (row) => row.id ?? row._id,
  initialPageSize,
  onRowClick,
  onSurveyClick,
  TableComponent,
  CardComponent,
  renderCard,
  CardProps,
  TableProps,
  ThumbProps,
  showPagination = true,
  toolbarRender,
  toolbarLeft,
  placeholder = '검색어를 입력해 주세요',
  onSearchSubmit,
  children,
  emptyMessage = '데이터가 없습니다.', 
  sort,
  onSort,
  showStudentDetails,
  forceTableView = false,
  showContent = true,
  caption,
  storageKey, // 페이지 저장용
}) {
  const [params, setParams] = useSearchParams()
  const { isMobile } = useIsMobile()

//  const [page, setPage] = useState(Number(params.get('page')) || 1)
  const getInitialPage = () => {
    if (storageKey) {
      const saved = sessionStorage.getItem(storageKey)
      if (saved) return Number(saved)
    }

    // 기존 URL 파라미터 fallback (있으면 유지)
    return Number(params.get('page')) || 1
  }

  const [page, setPage] = useState(getInitialPage)

  const [pageSize, setPageSize] = useState(initialPageSize)
  const [search, setSearch] = useState('')
  const [keyword, setKeyword] = useState('')
  const [data, setData] = useState([])
  const [total, setTotal] = useState(0)
  const [loading, setLoading] = useState(false)

  const View =
    view === 'card' ? CardComponent :
    TableComponent

  const totalPages = useMemo(() => Math.max(1, Math.ceil(total / pageSize)), [total, pageSize])

  const handlePageChange = (_, v) => {
    setPage(v)

    if (storageKey) {
      sessionStorage.setItem(storageKey, v)
    }
  }

  const handleSearch = useCallback(() => {
    setPage(1)
    if (storageKey) {
      sessionStorage.removeItem(storageKey)
    }
    setKeyword(search)
    onSearchSubmit?.(search)
  }, [search, onSearchSubmit, storageKey])

  useEffect(() => {
    let aborted = false
    async function run() {
      setLoading(true)
      try {
        if (fetcher) {
          const res = await fetcher({ page, pageSize, keyword })
          if (!aborted) {
            setData(res.items ?? [])
            setTotal(res.total ?? 0)
          }
        } else {
          setData(items ?? [])
          setTotal(items?.length ?? 0)
        }
      } finally {
        if (!aborted) setLoading(false)
      }
    }
    run()
    return () => { aborted = true }
  }, [fetcher, items, page, pageSize, keyword])

  return (
    <Stack>
      {/* 툴바 */}
      {toolbarRender ? (
        toolbarRender({
          search,
          setSearch,
          total,
          page,
          setPage,
          handleSearch,
          isMobile,
        })
      ) : (
        // 기본 검색바
        <Stack
          direction={isMobile ? 'column' : 'row'}
          alignItems="center"
          spacing={1}
          sx={{
            bgcolor: 'grey.50',
            borderRadius: 4,
            px: isMobile ? 2 : 6,
            py: isMobile ? 3 : 6,
            mb: 6,
          }}
        >
          {toolbarLeft && (
            <Stack direction="row" spacing={1} alignItems="center" sx={{ width: isMobile ? '100%' : 'auto' }}>
              {toolbarLeft}
            </Stack>
          )}

          <Stack direction={isMobile ? 'column' : 'row'} sx={{ flex: 'auto', width: isMobile ? '100%' : 'auto', gap: 2 }}>
            <Typography
              component="label"
              sx={visuallyHidden}
              htmlFor="search-input"
            >
              검색어 입력
            </Typography>
            <TextField
              id="search-input"
              size="small"
              placeholder={placeholder}
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter') {
                  e.preventDefault()
                  setKeyword(search)
                  onSearchSubmit?.(search)
                  setPage(1)
                  if (storageKey) sessionStorage.removeItem(storageKey)
                }
              }}
              sx={{ flex: 1 }}
            />
            <Button
              variant="contained"
              color="grey"
              size="medium"
              onClick={handleSearch}
            >
              검색
            </Button>
          </Stack>
        </Stack>
      )}

      {/* 전체 건수 */}
      {!toolbarRender && showPagination && (
        <Typography
          variant="body2"
          fontWeight={500}
          sx={{ mb: 1.5 }}
        >
          전체{' '}
          <Typography
            component="span"
            variant="body2"
            fontWeight={600}
            color="primary.main"
          >
            {total.toLocaleString()}
          </Typography>
          건
        </Typography>
      )}

      {/* 리스트 */} 
      <Box sx={{ position: 'relative', minHeight: '120px' }}>
        {loading && (
          <Stack alignItems="center" justifyContent="center" sx={{ position: 'absolute', inset: 0 }}>
            <LinearProgress />
          </Stack>
        )}

        {!loading && showContent && View && (
          <View
            caption={caption}
            rows={data}
            columns={columns}
            getRowId={getRowId}
            onRowClick={onRowClick}
            onSurveyClick={onSurveyClick}
            renderCard={renderCard}
            emptyMessage={emptyMessage}
            sort={sort}
            onSort={onSort}
            showStudentDetails={showStudentDetails}
            forceTableView={forceTableView} 
            {...(view === 'card' ? (CardProps || {}) :
              view === 'thumb' ? (ThumbProps || {}) :
              (TableProps || {}))}
          />
        )}
      </Box>

      {/* 페이지네이션 */}
      {/* {showPagination && total > 0 && totalPages >= 1 && showContent &&  (
        <Stack direction="row" alignItems="center" justifyContent="center" sx={{ mt: 5 }}>
          <Pagination
            page={page}
            count={totalPages}
            onChange={handlePageChange}
          //  showFirstButton
          //  showLastButton
            boundaryCount={isMobile ? 1 : 2}
            siblingCount={isMobile ? 0 : 1}
            sx={{ mt: 0 }}
          />
        </Stack>
      )} */}
      {/* 페이지네이션 */}
      {showPagination && total > 0 && totalPages >= 1 && showContent && (
        isMobile ? (
          <Stack spacing={2} alignItems="center" sx={{ mt: 5 }}>
            {/* 이전 / 다음 */}
            <Stack direction="row" spacing={2}>
              <Pagination
                page={page}
                count={totalPages}
                onChange={handlePageChange}
                sx={{
                  '& .MuiPaginationItem-previousNext': {
                    mx: 1.5,
                  },
                }}
                renderItem={(item) => {
                  if (item.type === 'previous') {
                    return (
                      <PaginationItem
                        {...item}
                        components={{
                          previous: () => (
                            <Stack direction="row" alignItems="center" spacing={0.5}>
                              <KeyboardArrowLeftIcon fontSize="small" />
                              <Typography variant="body2">이전</Typography>
                            </Stack>
                          ),
                        }}
                      />
                    )
                  }

                  if (item.type === 'next') {
                    return (
                      <PaginationItem
                        {...item}
                        components={{
                          next: () => (
                            <Stack direction="row" alignItems="center" spacing={0.5}>
                              <Typography variant="body2">다음</Typography>
                              <KeyboardArrowRightIcon fontSize="small" />
                            </Stack>
                          ),
                        }}
                      />
                    )
                  }

                  return null
                }}
              />
            </Stack>

            {/* 페이지 숫자 */}
            <Pagination
              page={page}
              count={totalPages}
              onChange={handlePageChange}
              siblingCount={0}
              boundaryCount={1}
              renderItem={(item) => {
                if (
                  item.type === 'page' ||
                  item.type === 'start-ellipsis' ||
                  item.type === 'end-ellipsis'
                ) {
                  return <PaginationItem {...item} />
                }

                return null
              }}
            />
          </Stack>
        ) : (
          /* PC */
          <Stack direction="row" justifyContent="center" sx={{ mt: 5 }}>
            <Pagination
              page={page}
              count={totalPages}
              onChange={handlePageChange}
              siblingCount={1}
              boundaryCount={2}
            />
          </Stack>
        )
      )}


      {children}
    </Stack>
  )
}
